import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Camera, LogOut, ChevronRight, Bell, KeyRound, Mail, FileText, Check, ShieldCheck, Sprout } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/Auth";
import { PageHeader, Avatar } from "@/components/Common";

const PRIVACY = [["all", "Tutti"], ["followers", "Solo chi mi segue"], ["none", "Nessuno"]];
const DM_PRIVACY = [["all", "Tutti, con richiesta"], ["connections", "Solo le mie connessioni"]];

function Group({ title, hint, children, id }: any) {
  return (
    <section className="space-y-2" data-testid={id}>
      <h2 className="label text-ink/55 px-1">{title}</h2>
      <div className="surface p-1">{children}</div>
      {hint && <p className="text-xs text-ink/58 px-1">{hint}</p>}
    </section>
  );
}

function Choice({ options, value, onPick, testPrefix }: any) {
  return (
    <div role="radiogroup">
      {options.map(([k, l]: string[]) => (
        <button key={k} role="radio" aria-checked={value === k} onClick={() => onPick(k)} className="row rounded-2xl" data-testid={`${testPrefix}-${k}`}>
          <span className="flex-1">{l}</span>
          {value === k && <Check size={18} className="text-[hsl(var(--primary))]" />}
        </button>
      ))}
    </div>
  );
}

function ProfileForm() {
  const { user, setUser } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);
  const [f, setF] = useState({ display_name: user.display_name || "", username: user.username || "", bio: user.bio || "", niche: user.niche || "", links: (user.links || []).join("\n"), niches: user.niches || [] });
  const [busy, setBusy] = useState(false);
  const [avatarPct, setAvatarPct] = useState<number | null>(null);
  const { data: cats } = useQuery({ queryKey: ["cats"], queryFn: () => api.get("/categories").then((r) => r.data) });
  const toggleNiche = (s: string) => setF((x: any) => ({ ...x, niches: x.niches.includes(s) ? x.niches.filter((n: string) => n !== s) : x.niches.length < 5 ? [...x.niches, s] : x.niches }));
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.patch("/me/profile", { ...f, links: f.links.split(/\n+/).map((s) => s.trim()).filter(Boolean) });
      setUser(data);
      toast.success("Profilo aggiornato");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const uploadAvatar = async (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("purpose", "avatar");
    setAvatarPct(0);
    try {
      await api.post("/media/upload", fd, { onUploadProgress: (e) => setAvatarPct(e.total ? Math.round((e.loaded / e.total) * 100) : 0) });
      const { data } = await api.get("/auth/me");
      setUser(data);
      toast.success("Foto aggiornata");
    } catch (err) { toast.error(errMsg(err)); } finally { setAvatarPct(null); }
  };
  const field = (label: string, el: React.ReactNode) => <label className="block"><span className="text-sm font-medium text-ink/75">{label}</span><div className="mt-1.5">{el}</div></label>;
  return (
    <form onSubmit={save} className="p-4 space-y-4">
      <div className="flex items-center gap-4">
        <Avatar user={user} size={64} />
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadAvatar(e.target.files[0])} data-testid="avatar-file-input" />
        <button type="button" className="btn btn-ghost" onClick={() => fileRef.current?.click()} data-testid="avatar-upload-btn"><Camera size={16} />{avatarPct !== null ? `${avatarPct}%` : "Cambia foto"}</button>
      </div>
      {field("Nome visualizzato", <input className="field" maxLength={50} value={f.display_name} onChange={(e) => setF({ ...f, display_name: e.target.value })} data-testid="settings-name-input" />)}
      {field("Username", <input className="field" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value.toLowerCase().replace(/[^a-z0-9_.]/g, "") })} data-testid="settings-username-input" />)}
      {field("Bio", <textarea className="field min-h-[88px]" maxLength={300} value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} data-testid="settings-bio-input" />)}
      {field("Tema principale", <select className="field" value={f.niche} onChange={(e) => setF({ ...f, niche: e.target.value })} data-testid="settings-niche-select">{(cats || []).map((c: any) => <option key={c.slug} value={c.slug}>{c.name}</option>)}</select>)}
      <div>
        <span className="text-sm font-medium text-ink/75">Temi che ti interessano nel feed (max 5)</span>
        <div className="flex flex-wrap gap-2 mt-2" data-testid="settings-niches">
          {(cats || []).map((c: any) => <button type="button" key={c.slug} onClick={() => toggleNiche(c.slug)} aria-pressed={f.niches.includes(c.slug)} data-testid={`settings-niche-${c.slug}`} className={`chip min-h-[36px] ${f.niches.includes(c.slug) ? "chip-violet" : "hover:bg-white/70"}`}>{c.name}</button>)}
        </div>
      </div>
      {field("Link (uno per riga, max 5)", <textarea className="field min-h-[72px]" value={f.links} onChange={(e) => setF({ ...f, links: e.target.value })} data-testid="settings-links-input" />)}
      <button className="btn btn-primary" disabled={busy} data-testid="settings-save-btn">Salva profilo</button>
    </form>
  );
}

function DeleteAccount() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [del, setDel] = useState({ password: "", confirm: "" });
  const go = async () => {
    try { await api.post("/me/delete", del); toast.success("Account eliminato"); await logout(); } catch (err) { toast.error(errMsg(err)); }
  };
  if (!open) return <button className="row rounded-2xl text-[hsl(var(--destructive))]" onClick={() => setOpen(true)} data-testid="delete-account-open-btn"><span className="flex-1 font-semibold">Elimina account</span><ChevronRight size={18} /></button>;
  return (
    <div className="p-4 space-y-3" data-testid="delete-account-section">
      <p className="text-sm text-ink/72">Contenuti, obiettivi, follow, connessioni e notifiche vengono rimossi e non potrai più accedere. L'operazione non si può annullare.</p>
      {user.auth_provider === "password" && <input type="password" className="field" placeholder="Password attuale" value={del.password} onChange={(e) => setDel({ ...del, password: e.target.value })} data-testid="delete-password-input" />}
      <input className="field" placeholder="Scrivi ELIMINA per confermare" value={del.confirm} onChange={(e) => setDel({ ...del, confirm: e.target.value })} data-testid="delete-confirm-input" />
      <div className="flex gap-2">
        <button className="btn btn-danger" disabled={del.confirm.trim().toUpperCase() !== "ELIMINA" || (user.auth_provider === "password" && !del.password)} onClick={go} data-testid="delete-account-btn">Elimina definitivamente</button>
        <button className="btn btn-ghost" onClick={() => setOpen(false)} data-testid="delete-account-cancel-btn">Annulla</button>
      </div>
    </div>
  );
}

const LinkRow = ({ to, icon: Icon, label, hint, id }: any) => (
  <Link to={to} className="row rounded-2xl" data-testid={id}><Icon size={18} className="text-ink/65 shrink-0" /><span className="flex-1 min-w-0"><span className="block font-medium">{label}</span>{hint && <span className="block text-xs text-ink/60">{hint}</span>}</span><ChevronRight size={18} className="text-ink/45" /></Link>
);

function ReminderSetting({ user, patch }: any) {
  const on = !!user.reminder_enabled;
  const set = (body: any) => patch({ ...body, tz: new Date().getTimezoneOffset() });
  return (
    <div data-testid="reminder-setting">
      <div className="row">
        <Sprout size={18} className="text-ink/65 shrink-0" />
        <label htmlFor="reminder-switch" className="flex-1 font-medium">Promemoria obiettivi di oggi</label>
        <Switch id="reminder-switch" checked={on} onCheckedChange={(v) => set({ reminder_enabled: v })} data-testid="reminder-toggle" />
      </div>
      {on && (
        <div className="row">
          <label htmlFor="reminder-time" className="flex-1 text-sm text-ink/75">Orario</label>
          <input id="reminder-time" type="time" className="field w-36 min-h-[40px] py-1.5" defaultValue={user.reminder_time || "20:00"}
            onBlur={(e) => e.target.value && e.target.value !== user.reminder_time && set({ reminder_time: e.target.value })} data-testid="reminder-time-input" />
        </div>
      )}
    </div>
  );
}

export default function Impostazioni() {
  const { user, setUser, logout } = useAuth();
  const { data: blocks, refetch } = useQuery({ queryKey: ["blocks"], queryFn: () => api.get("/me/blocks").then((r) => r.data) });
  const patch = async (body: any) => {
    try { const { data } = await api.patch("/me/settings", body); setUser(data); } catch (err) { toast.error(errMsg(err)); }
  };
  const unblock = async (id: string) => {
    try { await api.delete(`/users/${id}/block`); refetch(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div data-testid="settings-page">
      <PageHeader title="Impostazioni" back="/profilo" />
      <div className="px-5 md:px-10 max-w-2xl space-y-8 pb-14">
        <Group title="Account e informazioni personali" id="settings-account"><ProfileForm /></Group>
        <Group title="Privacy e visibilità" id="settings-privacy" hint="Massimo 20 richieste al giorno per persona. Dopo un rifiuto serve una pausa prima di riprovare.">
          <div className="px-4 pt-3 pb-1 text-sm font-medium text-ink/75">Chi può proporti una connessione (Bussa)</div>
          <Choice options={PRIVACY} value={user.bussa_privacy || "all"} onPick={(v: string) => patch({ bussa_privacy: v })} testPrefix="bussa-privacy" />
        </Group>
        <Group title="Messaggi" id="settings-messages" hint={(user.dm_privacy || "all") === "all" ? "Chi non è tra le tue connessioni finisce nelle Richieste: decidi tu se accettare." : "Solo le tue connessioni possono iniziare una conversazione."}>
          <div className="px-4 pt-3 pb-1 text-sm font-medium text-ink/75">Chi può scriverti</div>
          <Choice options={DM_PRIVACY} value={user.dm_privacy || "all"} onPick={(v: string) => patch({ dm_privacy: v })} testPrefix="dm-privacy" />
        </Group>
        <Group title="Utenti bloccati" id="settings-blocked">
          {!blocks?.length ? <div className="row text-sm text-ink/62" data-testid="blocked-empty">Nessun utente bloccato.</div> : (
            <div data-testid="blocked-list">{blocks.map((b: any) => <div key={b.id} className="row"><Avatar user={b} size={32} /><span className="flex-1">@{b.username}</span><button className="btn btn-ghost min-h-[36px] px-3 py-1 text-xs" onClick={() => unblock(b.id)} data-testid="unblock-btn">Sblocca</button></div>)}</div>
          )}
        </Group>
        <Group title="Notifiche" id="settings-notifications" hint="Il promemoria arriva tra le notifiche dell'app solo se un obiettivo giornaliero non è ancora segnato.">
          <LinkRow to="/attivita?tab=impostazioni" icon={Bell} label="Scegli quali notifiche ricevere" id="settings-notif-link" />
          <ReminderSetting user={user} patch={patch} />
        </Group>
        <Group title="Sicurezza" id="settings-security">
          <div className="row"><Mail size={18} className="text-ink/65" /><span className="flex-1 min-w-0 truncate">{user.email}</span><span className={`chip ${user.email_verified ? "chip-lime" : ""}`} data-testid="settings-email-status">{user.email_verified ? <><ShieldCheck size={12} />Verificata</> : "Da verificare"}</span></div>
          {user.auth_provider === "password" && <LinkRow to="/password-dimenticata" icon={KeyRound} label="Cambia password" hint="Ti inviamo un link via email" id="settings-password-link" />}
        </Group>
        <Group title="Gestione dati" id="settings-data">
          <LinkRow to="/privacy" icon={FileText} label="Informativa privacy" id="settings-privacy-doc" />
          <LinkRow to="/termini" icon={FileText} label="Termini di servizio" id="settings-terms-doc" />
          <DeleteAccount />
        </Group>
        <button className="btn btn-ghost w-full" onClick={logout} data-testid="logout-btn"><LogOut size={16} />Esci dall'account</button>
      </div>
    </div>
  );
}
